import { MONTH_NAMES, WEEKDAYS } from "../../../constants/common/common";
import {
  setDeliveryDateErrorFromAddToCart,
  setDeliveryTimeSlotErrorFromAddToCart,
  setPersonalizationErrorFromAddToCart,
  setPincodeErrorFromAddToCart,
} from "../../../store/slices/pdpSlice";
import { dateToDMY, formatDateRange, getDates } from "../../common/deliveryDate";
import isEmpty from "lodash.isempty";

/**
 *
 * @param {*} string
 * @returns String with first alphabet Capital
 */
export const capitalizeFirstLetter = (string) => {
  return string?.toLowerCase().replace(/^./, (match) => match.toUpperCase());
};

/**
 * This method is to filter out flavour from cake product
 *
 */
export const generateFlavorTypeList = (variantProducts) => {
  const uniqueFlavors = [];

  variantProducts?.forEach((variant) => {
    const key = Object.keys(variant)?.[0];

    if (key) {
      const [, , flavor] = key.split("|");
      if (flavor) {
        const trimmedFlavor = flavor.trim();
        if (!uniqueFlavors.includes(trimmedFlavor)) {
          uniqueFlavors.push(trimmedFlavor);
        }
      }
    }
  });

  return uniqueFlavors;
};

/**
 *
 * @param {*} pincode
 * @param {*} deliveryDetailsData
 * @param {*} userPersonalization
 * @param {*} dispatch
 * @returns
 */
export const validateAllRequiredFieldsToAddToCart = (
  pincode,
  deliveryDetailsData,
  personalizationValidation,
  selectedDate,
  dispatch,
) => {
  let hasError = false;

  /**
   * @param {string} elementId
   */
  const scrollToElement = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  if (!pincode) {
    scrollToElement("location_lock");
    dispatch(setPincodeErrorFromAddToCart(true));
    hasError = true;
  } else if (deliveryDetailsData) {
    scrollToElement("delivery_date_slot");
    if (!selectedDate) {
      dispatch(setDeliveryDateErrorFromAddToCart(true));
    } else if (selectedDate) {
      dispatch(setDeliveryTimeSlotErrorFromAddToCart(true));
    }
    hasError = true;
  } else if (!personalizationValidation) {
    scrollToElement("personalization_section");
    dispatch(setPersonalizationErrorFromAddToCart(true));
    hasError = true;
  } else if (!hasError) {
    dispatch(setPincodeErrorFromAddToCart(false));
    dispatch(setDeliveryDateErrorFromAddToCart(false));
    dispatch(setPersonalizationErrorFromAddToCart(false));
  }

  return hasError;
};

/**
 * This method is used to check the product is digital or not
 *
 * @returns {boolean}  will return true or false
 */
export const checkIsDigitalProduct = (currentItem) => {
  if (currentItem?.shippingDisplayOptions && !currentItem?.shippingDisplayOptions.showPincodeOrCity) {
    return Object.keys(currentItem?.shippingDisplayOptions?.defaultGeoDetails).length > 0;
  }
  return false;
};

/**
 * This method is used to check that product is personalized type or not
 *
 * @param {object} product
 * @returns {boolean} is express product or not .
 */
export const isPersonalizedProduct = (product) => product?.personalizedInfo?.personalizedProduct;

/**
 * Function to render a bottom border line, with visibility based on screen size.
 * The border is hidden on mobile by default and shown on desktop, or vice versa based on the desktopVisibility argument.
 *
 * @param {boolean} [desktopVisibility=false] - Determines the visibility of the border on desktop screens.
 *                                              If true, the border is hidden on desktop and shown on mobile.
 *                                              If false, the border is shown on desktop and hidden on mobile.
 * @returns {JSX.Element} A div element with a bottom border and background color.
 */
export const borderLine = (desktopVisibility = false) => (
  <div className={`border-b-3 border-lightGrey bg-grey-100 ${desktopVisibility ? "hidden md:block" : "md:hidden"}`} />
);

/**
 * Generates a multi-order date range.
 * @param {Date} startDateString - The start date for the range.
 * @param {number} noOfDays - Number of days in the range.
 * @param {string} type - Frequency type (e.g., "DAILY").
 * @returns {Array} Array of formatted dates.
 */
export const getMultiOrderDateRange = (startDateString, { noOfDays, type }) => {
  if (!noOfDays || !startDateString || type !== "DAILY") return [];

  // Convert startDate string to Date object
  const [day, month, year] = startDateString.split("-").map(Number);
  const startDate = new Date(year, month - 1, day);

  // Generate the date range
  return Array.from({ length: noOfDays }, (_, index) => {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + index);
    return dateToDMY(date);
  });
};

/**
 *
 * @param {string} date that needs to be converted into string.
 * @param {boolean} forChip if it is for the pdp date chips.
 * @param {boolean} isSaved if the delivery details are saved on pdp.
 * @param {object} currentItem the selected item by the user.
 * @param {array} multiProductDateRange the array for delivery range of multi products.
 * @returns a string converting the dates to basic strings
 */
export const renderDateString = (
  date,
  currentItem = {},
  multiProductDateRange = [],
  forChip = false,
  isSaved = false,
) => {
  if (date) {
    let formattedDate = "";
    const dateString = date;
    const [day, month, year] = (dateString ?? "").split("-");

    const dateVar = new Date(year, month - 1, day);

    const weekday = WEEKDAYS[dateVar.getDay()]?.slice(0, 3);
    const monthName = MONTH_NAMES[dateVar.getMonth()]?.slice(0, 3);

    if (forChip) {
      formattedDate = currentItem?.isMultiOrderProduct ? formatDateRange(multiProductDateRange) : `${day} ${monthName}`;
    } else if (isSaved) {
      formattedDate = currentItem?.isMultiOrderProduct
        ? formatDateRange(multiProductDateRange)
        : `${weekday}, ${day} ${monthName}`;
    } else {
      formattedDate = currentItem?.isMultiOrderProduct
        ? formatDateRange(multiProductDateRange)
        : `${day}th  ${monthName}, ${weekday}`;
    }
    return formattedDate;
  } else {
    return "";
  }
};

/** Mirrors RedirectionToPLPComponent nudge visibility for CleverTap payloads (same-day / limited-catalogue). */
export const getRedirectionNudgeVisibility = ({
  isFromCMS = null,
  deliveryDatesList = [],
  isCutoffTimestamp,
  isCalender,
  isDisplayCatalogContent,
  limitedCatalogContent,
  blockedDates,
} = {}) => {
  const jsonBlockedDates =
    blockedDates?.length > 0 ? JSON.parse(blockedDates.replace(/(\d{1,2})-(\d{1,2})-(\d{4})/g, '"$&"')) : [];
  const isLimitedCatalog = isEmpty(limitedCatalogContent);
  const hasBlockedDate = deliveryDatesList.some((date) => jsonBlockedDates?.includes(date));

  const isRedirectToCatalog = Boolean(
    (isFromCMS && (deliveryDatesList.indexOf(dateToDMY(new Date())) === -1 || hasBlockedDate || !isCalender)) ||
    !getDates(jsonBlockedDates) ||
    !isDisplayCatalogContent ||
    isLimitedCatalog,
  );

  const rendersNothing = isFromCMS === null;
  const isLimitedCatalogueNudgeVisible = !rendersNothing && !isRedirectToCatalog;
  const isSameDayNudgeVisible =
    !rendersNothing &&
    isRedirectToCatalog &&
    deliveryDatesList.length > 0 &&
    deliveryDatesList.indexOf(dateToDMY(new Date())) === -1 &&
    Boolean(isCutoffTimestamp);

  return { isRedirectToCatalog, isSameDayNudgeVisible, isLimitedCatalogueNudgeVisible };
};
